import React, { useEffect, useState } from "react";

// ── CAPTURING THE INSTALL PROMPT ──
// The browser fires `beforeinstallprompt` ONCE, early, usually before any of
// these buttons has mounted. A button that only starts listening on mount —
// one on a page reached by in-app navigation, such as Refer & Earn — would
// therefore never see the event and could never appear.
//
// So the event is captured here at module scope, which runs when the app
// loads, and every button that mounts later picks it up. Nothing else about
// the button changes: it still only appears when the browser offers an
// install, and still hides once the app is installed.
let savedPrompt = null;
const subscribers = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    savedPrompt = e;
    subscribers.forEach((notify) => notify(e));
  });
  window.addEventListener("appinstalled", () => {
    savedPrompt = null;
    subscribers.forEach((notify) => notify(null));
  });
}

// `inline` places the SAME button inside a card instead of floating it in the
// corner. Everything else — when it appears, when it hides, what the click
// does — is untouched, so the default (floating) usage on Login and Home is
// exactly what it always was.
const InstallButton = ({ inline = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showButton, setShowButton] = useState(false);

  // Function to check if app is already installed
  const checkInstalled = () => {
    return (
      window.matchMedia("(display-mode: standalone)").matches || // Android PWA
      window.navigator.standalone === true // iOS
    );
  };

  useEffect(() => {
    // Hide button if already installed
    if (checkInstalled()) {
      setShowButton(false);
      return undefined;
    }

    // An install prompt the browser offered BEFORE this button mounted.
    if (savedPrompt) {
      setDeferredPrompt(savedPrompt);
      setShowButton(true);
    }

    // ...and any that arrives, or is spent, while it is on screen.
    const notify = (e) => {
      if (e && !checkInstalled()) {
        setDeferredPrompt(e);
        setShowButton(true);
      } else {
        setDeferredPrompt(null);
        setShowButton(false);
      }
    };

    subscribers.add(notify);
    return () => subscribers.delete(notify);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        console.log("User installed app!");
      } else {
        console.log("User dismissed install");
      }
      // A prompt can only be used once, so it is cleared everywhere — including
      // for any other button currently on screen.
      savedPrompt = null;
      subscribers.forEach((notify) => notify(null));
      setDeferredPrompt(null);
      setShowButton(false);
    } else {
      // fallback: open website
      window.open("https://theblogssphere.com/", "_blank");
    }
  };

  if (!showButton) return null;

  return (
    <button
      onClick={handleInstallClick}
      style={
        inline
          ? {
              alignSelf: "flex-start",
              marginTop: "8px",
              padding: "10px 20px",
              backgroundColor: "#0f172a",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }
          : {
              position: "fixed",
              bottom: "20px",
              right: "20px",
              padding: "10px 20px",
              backgroundColor: "#0f172a",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              zIndex: 9999,
            }
      }
    >
      Install App For Fast Access
    </button>
  );
};

export default InstallButton;